import { __assign, __read, __rest, __spread } from "tslib";
import React from "react";
import classNames from "classnames";
import { Icon } from "../../icon";
import { getRowKeyFromRecordKey } from "../util/get-row-key-from-record-key";
import { useMiddleware } from "../util/use-middleware";
import { injectPropsIfTargetNotExisted } from "../util/inject-props-if-target-not-existed";
import { useConfig } from "../../_util/config-context";
var fallbackColumnKey = "__expandable_addon__";
var rowDisabled = null;
export function expandable(options) {
    var render = options.render, expand = options.expand, method = options.method, shouldRecordExpandable = options.shouldRecordExpandable, expandedKeys = options.expandedKeys, onExpandedKeysChange = options.onExpandedKeysChange, targetColumnKey = options.targetColumnKey, gapCell = options.gapCell, gapCellRender = options.gapCellRender, _a = options.width, width = _a === void 0 ? 26 : _a, _b = options.iconRender, iconRender = _b === void 0 ? function (x) { return x; } : _b, rowExpand = options.rowExpand;
    var expandedKeySet = new Set(expandedKeys || []);
    var getRowKey;
    var addons = [];
    var extraProps = {
        recordsExpandable: !!expand,
    };
    return {
        getInfo: function () { return ({ name: "expandable" }); },
        onInjectProps: function (props) {
            var recordKey = props.recordKey;
            getRowKey = getRowKeyFromRecordKey(recordKey);
            /* eslint-disable prefer-destructuring */
            rowDisabled = props.rowDisabled || (function () { return false; });
            addons = props.addons;
            var columns = injectPropsIfTargetNotExisted(props.columns, targetColumnKey, {
                key: fallbackColumnKey,
                width: width,
                header: null,
                render: function () { return null; },
            });
            /* eslint-enable prefer-destructuring */
            return __assign(__assign(__assign({}, props), { columns: columns }), extraProps);
        },
        onInjectColumn: function (previous) { return function (record, rowKey, recordIndex, column, columnIndex) {
            // 不是目标列
            if (column.key !== targetColumnKey && column.key !== fallbackColumnKey) {
                return previous(record, rowKey, recordIndex, column, columnIndex);
            }
            var _a = previous(record, rowKey, recordIndex, column, columnIndex), preChildren = _a.children, props = _a.props, result = __rest(_a, ["children", "props"]);
            var children = preChildren;
            // 非表头
            if (recordIndex !== -1) {
                var key_1 = getRowKey(record, recordIndex);
                var isExpandable = !shouldRecordExpandable || shouldRecordExpandable(record);
                var isExpanded_1 = expandedKeySet.has(key_1);
                if (isExpandable) {
                    var toggle = function (event) {
                        if (isExpanded_1) {
                            var nextSet = new Set(expandedKeySet);
                            nextSet.delete(key_1);
                            onExpandedKeysChange(Array.from(nextSet.keys()), {
                                operateType: "collapse",
                                operateKey: key_1,
                                operateRecord: record,
                                event: event,
                            });
                        }
                        else {
                            onExpandedKeysChange(expandedKeys.concat(key_1), {
                                operateType: "expand",
                                operateKey: key_1,
                                operateRecord: record,
                                event: event,
                            });
                        }
                    };
                    var element = (React.createElement("div", null,
                        React.createElement(IconWrap, { onClick: toggle },
                            React.createElement(Icon, { type: isExpanded_1 ? "arrowdown" : "arrowright" })),
                        children));
                    children = iconRender(element, {
                        children: preChildren,
                        record: record,
                        rowKey: rowKey,
                        recordIndex: recordIndex,
                        disabled: rowDisabled(record),
                    });
                }
            }
            return __assign(__assign({}, result), { props: props, children: children });
        }; },
        onInjectRow: function (renderRow) { return function (record, rowKey, recordIndex, columns) {
            var key = getRowKey(record, recordIndex);
            var isExpandable = !shouldRecordExpandable || shouldRecordExpandable(record);
            var onClick = function (e) { return e; };
            if (rowExpand && isExpandable) {
                var isExpanded_2 = expandedKeySet.has(key);
                onClick = function (event) {
                    if (isExpanded_2) {
                        var nextSet = new Set(expandedKeySet);
                        nextSet.delete(key);
                        onExpandedKeysChange(Array.from(nextSet.keys()), {
                            operateType: "collapse",
                            operateKey: key,
                            operateRecord: record,
                            event: event,
                        });
                    }
                    else {
                        onExpandedKeysChange(expandedKeys.concat(key), {
                            operateType: "expand",
                            operateKey: key,
                            operateRecord: record,
                            event: event,
                        });
                    }
                };
            }
            // 不是展开的行，原样渲染即可
            if (!expandedKeySet.has(rowKey)) {
                var _a = renderRow(record, rowKey, recordIndex, columns), prepends_1 = _a.prepends, row_1 = _a.row, appends_1 = _a.appends;
                return {
                    prepends: prepends_1,
                    row: React.cloneElement(row_1, {
                        onClick: function (event) {
                            if (typeof row_1.props.onClick === "function") {
                                row_1.props.onClick(event);
                            }
                            onClick(event);
                        },
                    }),
                    appends: appends_1,
                };
            }
            // 原始行的渲染内容
            var _b = renderRow(record, rowKey, recordIndex, columns), prepends = _b.prepends, row = _b.row, appends = _b.appends;
            // 展开的内容
            var expands = [];
            // 支持两种展开方式，提供了 render 方法的话，渲染一个行给 render() 作为渲染容器
            if (typeof render === "function" &&
                (!method || method(record) === "render")) {
                expands.push(React.createElement("tr", { className: "tr__detailrow", key: rowKey + "__expanded-row" },
                    gapCell > 0 && (React.createElement("td", { colSpan: gapCell }, gapCellRender && React.createElement("div", null, gapCellRender(record)))),
                    React.createElement("td", { colSpan: columns.length - (gapCell > 0 ? gapCell : 0) },
                        React.createElement("div", null, render(record)))));
            }
            // 如果提供了 expand 方法，则意图为展开当前数据为更多的数据
            // 更多的数据走原来表格的渲染逻辑
            else if (typeof expand === "function" &&
                (!method || method(record) === "expand")) {
                // 展开的数据
                var expandedRecords = expand(record) || [];
                // 展开数据的渲染和追加
                expandedRecords.forEach(function (expandedRecord, expandedRecordIndex) {
                    var expandedRowKey = getRowKey(expandedRecord, expandedRecordIndex);
                    // 这里如果直接使用 renderRow，则展开的数据无法经过插件的处理，所以调用了 useMiddleware
                    var expandedInfo = useMiddleware(addons, "onInjectRow")(renderRow)(expandedRecord, expandedRowKey, recordIndex, columns);
                    expands.push.apply(expands, __spread(expandedInfo.prepends));
                    expands.push(expandedInfo.row);
                    expands.push.apply(expands, __spread(expandedInfo.appends));
                });
            }
            return {
                prepends: prepends,
                row: React.cloneElement(row, {
                    className: classNames(row.props.className, "tr__masterrow"),
                    onClick: function (event) {
                        if (typeof row.props.onClick === "function") {
                            row.props.onClick(event);
                        }
                        onClick(event);
                    },
                }),
                appends: __spread(appends, expands),
            };
        }; },
    };
}
function IconWrap(_a) {
    var onClick = _a.onClick, children = _a.children;
    var classPrefix = useConfig().classPrefix;
    return (React.createElement("a", { className: classPrefix + "-table__icon-wrap", onClick: onClick }, children));
}
//# sourceMappingURL=expandable.js.map